import React, { useState, useEffect } from 'react';
import { 
  X, Image as ImageIcon, Video, Search, RefreshCw, Check, 
  ExternalLink, HardDrive, FileQuestion, Play, Info
} from 'lucide-react';
import { MediaItem } from '../../types';

interface MediaPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (url: string) => void;
  allowedType?: 'image' | 'video' | 'all';
  title?: string;
}

export const MediaPickerModal: React.FC<MediaPickerModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  allowedType = 'all',
  title = 'Избор на файл от папка public'
}) => {
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'image' | 'video'>(
    allowedType === 'all' ? 'all' : allowedType
  );
  const [selectedUrl, setSelectedUrl] = useState<string | null>(null);

  const fetchMedia = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/media');
      if (res.ok) {
        const data = await res.json();
        setMediaItems(data.files || []);
      }
    } catch (e) {
      console.error('Грешка при зареждане на медийни файлове:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchMedia();
      setSelectedUrl(null);
      if (allowedType !== 'all') {
        setFilterType(allowedType);
      }
    }
  }, [isOpen, allowedType]);

  if (!isOpen) return null;

  const filteredItems = mediaItems.filter(item => {
    if (filterType !== 'all' && item.type !== filterType) {
      return false;
    }
    if (search.trim()) {
      const query = search.toLowerCase();
      return (
        item.name.toLowerCase().includes(query) ||
        item.url.toLowerCase().includes(query) ||
        item.extension.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const handleConfirmSelect = () => {
    if (selectedUrl) {
      onSelect(selectedUrl);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-sm z-[100] flex items-center justify-center p-3 sm:p-5">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl border border-slate-200 overflow-hidden font-sans">
        
        {/* Modal Header */}
        <div className="bg-[#0F1E32] text-white p-4 sm:p-5 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-[#C5A46E]/20 text-[#C5A46E] flex items-center justify-center">
              <HardDrive className="w-4 h-4" />
            </div>
            <div>
              <h3 className="font-bold text-sm tracking-wide text-white">{title}</h3>
              <p className="text-[11px] text-slate-400">
                Автоматично открити снимки и видеа от папка <code className="text-[#C5A46E]">public/</code> ({mediaItems.length} общо)
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Toolbar: Search & Filters */}
        <div className="p-3 sm:p-4 bg-slate-50 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3 shrink-0">
          <div className="flex items-center gap-2 flex-grow max-w-md">
            <div className="relative flex-grow">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Търсене по име на файл..."
                className="w-full pl-8 pr-3 py-1.5 text-xs bg-white border border-slate-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#C5A46E]"
              />
            </div>
            <button
              onClick={fetchMedia}
              disabled={loading}
              className="p-2 bg-white border border-slate-300 hover:bg-slate-100 rounded-lg text-slate-600 transition cursor-pointer"
              title="Презареди файловете от public"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin text-[#C5A46E]' : ''}`} />
            </button>
          </div>

          {/* Type Filter Buttons */}
          <div className="flex items-center gap-1.5">
            {allowedType === 'all' && (
              <>
                <button
                  onClick={() => setFilterType('all')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${
                    filterType === 'all' 
                      ? 'bg-[#0F1E32] text-white' 
                      : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  Всички ({mediaItems.length})
                </button>
                <button
                  onClick={() => setFilterType('image')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1 cursor-pointer ${
                    filterType === 'image' 
                      ? 'bg-[#0F1E32] text-white' 
                      : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <ImageIcon className="w-3 h-3 text-[#C5A46E]" />
                  Снимки ({mediaItems.filter(i => i.type === 'image').length})
                </button>
                <button
                  onClick={() => setFilterType('video')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1 cursor-pointer ${
                    filterType === 'video' 
                      ? 'bg-[#0F1E32] text-white' 
                      : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <Video className="w-3 h-3 text-sky-500" />
                  Видеа ({mediaItems.filter(i => i.type === 'video').length})
                </button>
              </>
            )}

          </div>
        </div>

        {/* GitHub Upload Info Banner */}
        <div className="px-4 py-2.5 bg-amber-50/90 border-b border-amber-200 text-amber-900 text-xs flex items-center gap-2 shrink-0 leading-relaxed">
          <Info className="w-4 h-4 text-amber-600 shrink-0" />
          <span>
            Нови снимки се добавят чрез качване в <code className="bg-amber-100/90 px-1 py-0.5 rounded font-mono font-bold text-amber-950">public/</code> папката на проекта (през GitHub). След автоматичния redeploy на Cloudflare те ще се появят тук автоматично.
          </span>
        </div>

        {/* Media Grid */}
        <div className="p-4 sm:p-5 overflow-y-auto flex-grow bg-slate-100/50">
          {loading && mediaItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-slate-400">
              <RefreshCw className="w-8 h-8 animate-spin text-[#C5A46E] mb-2" />
              <p className="text-xs">Сканиране на папка public/ за снимки и видеа...</p>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center text-slate-400">
              <FileQuestion className="w-12 h-12 text-slate-300 mb-2" />
              <p className="text-sm font-bold text-slate-600">Няма намерени файлове</p>
              <p className="text-xs max-w-sm mt-1">
                Поставете снимки или видеа в папка <code className="text-[#0F1E32] font-mono font-bold">public/</code> на проекта и натиснете бутона за презареждане.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3.5">
              {filteredItems.map((item) => {
                const isSelected = selectedUrl === item.url;
                return (
                  <div
                    key={item.id}
                    onClick={() => setSelectedUrl(item.url)}
                    onDoubleClick={() => {
                      onSelect(item.url);
                      onClose();
                    }}
                    className={`group relative bg-white rounded-xl border-2 overflow-hidden transition-all duration-150 cursor-pointer flex flex-col ${
                      isSelected
                        ? 'border-[#C5A46E] ring-2 ring-[#C5A46E]/30 shadow-md'
                        : 'border-slate-200 hover:border-slate-300 hover:shadow-sm'
                    }`}
                  >
                    {/* Media Preview Box */}
                    <div className="aspect-video bg-slate-100 relative overflow-hidden flex items-center justify-center">
                      {item.type === 'image' ? (
                        <img
                          src={item.url}
                          alt={item.name}
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=100&q=80';
                          }}
                        />
                      ) : (
                        <div className="w-full h-full bg-slate-900 flex flex-col items-center justify-center text-white relative">
                          <Play className="w-8 h-8 text-[#C5A46E] opacity-90 group-hover:scale-110 transition-transform" />
                          <span className="text-[10px] font-mono text-slate-300 mt-1 uppercase font-bold">Видео файл</span>
                          <video
                            src={item.url}
                            preload="metadata"
                            muted
                            className="absolute inset-0 w-full h-full object-cover opacity-30"
                          />
                        </div>
                      )}

                      {/* Top Badges */}
                      <div className="absolute top-2 left-2 flex gap-1 items-center">
                        <span className={`px-1.5 py-0.5 rounded text-[9px] font-black uppercase text-white shadow-sm ${
                          item.type === 'image' ? 'bg-[#C5A46E]' : 'bg-sky-600'
                        }`}>
                          {item.type === 'image' ? 'Снимка' : 'Видео'}
                        </span>
                        <span className="px-1.5 py-0.5 rounded text-[9px] font-mono bg-black/60 text-slate-200 uppercase font-bold backdrop-blur-xs">
                          .{item.extension}
                        </span>
                      </div>

                      {isSelected && (
                        <div className="absolute inset-0 bg-[#C5A46E]/20 flex items-center justify-center">
                          <div className="w-7 h-7 rounded-full bg-[#C5A46E] text-white flex items-center justify-center shadow-lg">
                            <Check className="w-4 h-4 stroke-[3]" />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* File Details */}
                    <div className="p-2.5 flex flex-col flex-grow bg-white">
                      <p className="text-xs font-bold text-slate-800 truncate" title={item.name}>
                        {item.name}
                      </p>
                      <div className="flex items-center justify-between text-[10px] text-slate-400 mt-1">
                        <span className="font-mono">{item.size}</span>
                        <span className="font-mono text-[#C5A46E] font-bold">{item.url}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="p-3 sm:p-4 bg-white border-t border-slate-200 flex items-center justify-between shrink-0">
          <div className="text-xs text-slate-500 truncate max-w-sm">
            {selectedUrl ? (
              <span className="flex items-center gap-1.5 font-mono text-[#0F1E32]">
                <Check className="w-3.5 h-3.5 text-emerald-600" />
                Избран файл: <strong>{selectedUrl}</strong>
              </span>
            ) : (
              <span>Кликнете върху файл за предварителен избор</span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-lg transition cursor-pointer"
            >
              Отказ
            </button>
            <button
              onClick={handleConfirmSelect}
              disabled={!selectedUrl}
              className={`px-5 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition shadow cursor-pointer ${
                selectedUrl
                  ? 'bg-[#0F1E32] hover:bg-[#C5A46E] text-white'
                  : 'bg-slate-200 text-slate-400 cursor-not-allowed'
              }`}
            >
              Постави в сайта ✅
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};
